import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import GuidedDecisionView from '../components/proposals/GuidedDecisionView';
import ClassicEngineView from '../components/proposals/ClassicEngineView';
import {
  CheckCircle2, Loader2, Clock, Globe, CreditCard, Monitor,
  Wrench, Lightbulb, ArrowRight, X, AlertTriangle, Headphones,
  Users, Printer, Settings
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function ProposalViewPage() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/proposals/public/${token}`);
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Proposal not found');
      }
      const data = await resp.json();
      setProposal(data);
      if (data.status === 'accepted') setAccepted(true);
      if (data.is_expired) setIsExpired(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Countdown timer
  useEffect(() => {
    if (!proposal?.expires_at || isExpired || accepted) return;
    const timer = setInterval(() => {
      const now = new Date();
      const expires = new Date(proposal.expires_at);
      const diff = expires - now;
      if (diff <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
        clearInterval(timer);
      } else {
        setTimeLeft({
          hours: Math.floor(diff / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [proposal?.expires_at, isExpired, accepted]);

  const handleAccept = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/proposals/public/${token}/accept`, { method: 'POST' });
      const data = await resp.json();
      if (!resp.ok) { alert(data.detail || 'Something went wrong — please contact your rep'); return null; }
      setAccepted(true);
      if (data.application_url) {
        setTimeout(() => { window.location.href = data.application_url; }, 1200);
      }
      return data;
    } catch (e) {
      alert('Failed to accept proposal');
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fc]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[var(--brand)] mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading your proposal...</p>
        </div>
      </div>
    );
  }

  if (error && !proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fc]">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Proposal Not Found</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (isExpired && !accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fc]">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md">
          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Proposal Expired</h2>
          <p className="text-gray-500 mb-6">This proposal is no longer available. Contact your representative for an updated offer.</p>
          <div className="text-xs text-gray-300">Ref: {proposal?.proposal_id}</div>
        </div>
      </div>
    );
  }

  const p = proposal;
  if (!p) return null;
  if (p.engine?.template === 'classic') return <ClassicEngineView p={p} accepted={accepted} onAccept={handleAccept} />;
  if (p.engine) return <GuidedDecisionView p={p} accepted={accepted} onAccept={handleAccept} />;

  const pricing = p.pricing_data || {};
  const hw = p.hardware_options || [];
  const allOptions = p.all_pricing_options || [pricing];
  const volume = p.monthly_volume || 0;
  const currentFees = p.current_fees || 0;
  const currentRate = (p.current_effective_rate || 0) * 100;

  // Tenant branding embedded in proposal
  const brand = p.tenant_branding || {};
  const brandColor = brand.primary_color || '#1a1f4e';
  const brandName = brand.company_name || 'Merchant Solutions Corp';
  const brandLogo = brand.logo_url;
  const brandFont = brand.font || 'Inter';

  const feeDist = p.fee_distribution || {
    interchange_assessments: Math.round(volume * 0.018 * 100) / 100,
    processor_markup: Math.max(0, Math.round((currentFees - volume * 0.018 - Math.min(volume * 0.002, 189)) * 100) / 100),
    hidden_fees: Math.round(Math.min(volume * 0.002, 189) * 100) / 100,
  };

  const isDualPricing = pricing.model === 'dual_pricing';
  const qualifiesFreeEquipment = isDualPricing && hw.length > 0;

  const pieData = [
    { name: 'Interchange + Assessments', value: feeDist.interchange_assessments, color: '#dc2626', desc: 'Non-negotiable card brand fees' },
    { name: 'Processor Markup', value: feeDist.processor_markup, color: '#f472b6', desc: "Current provider's profit margin" },
    { name: 'Hidden Fees', value: feeDist.hidden_fees, color: '#d1d5db', desc: 'Junk fees, PCI, statement fees' },
  ];

  const newRate = pricing.effective_rate ? (pricing.effective_rate * 100).toFixed(2) : '0.00';
  const monthlySavings = pricing.merchant_savings || 0;
  const annualSavings = monthlySavings * 12;
  const newMonthlyCost = pricing.merchant_monthly_cost || 0;

  return (
    <div className="min-h-screen bg-white" data-testid="proposal-view-page" style={{ fontFamily: `'${brandFont}', 'Segoe UI', system-ui, sans-serif`, '--brand': brandColor }}>

      {/* ═══ HEADER ═══ */}
      <header className="text-white sticky top-0 z-50 shadow-lg" style={{ backgroundColor: brandColor }} data-testid="proposal-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {brandLogo && (
                <img src={brandLogo} alt={brandName} className="h-8 w-auto object-contain" data-testid="proposal-brand-logo" />
              )}
              <span className="font-bold text-lg tracking-wide" data-testid="proposal-brand-name">{brandName}</span>
            </div>
            <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
              <button onClick={() => scrollTo('who-we-are')} className="text-white/70 hover:text-white transition-colors">Who We Are</button>
              <button onClick={() => scrollTo('analysis')} className="text-white/70 hover:text-white transition-colors">Analysis</button>
              <button onClick={() => scrollTo('recommendations')} className="text-white/70 hover:text-white transition-colors">Recommendations</button>
              <button onClick={() => scrollTo('equipment')} className="text-white/70 hover:text-white transition-colors">Equipment</button>
            </nav>
            <button
              onClick={() => scrollTo('cta')}
              className="px-5 py-2 rounded-lg text-sm font-bold hover:opacity-90 transition-all hover:shadow-md"
              style={{ backgroundColor: 'white', color: brandColor }}
              data-testid="header-get-started-btn"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {p.cover_image_url && (
        <div className="w-full bg-gray-100" data-testid="proposal-cover">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${p.cover_image_url}`} alt="" className="mx-auto max-h-[420px] w-full max-w-7xl object-cover" data-testid="proposal-cover-image" />
        </div>
      )}

      {/* ═══ HERO SECTION ═══ */}
      <section id="who-we-are" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-6" style={{ color: `${brandColor}80` }}>
            Custom Proposal Prepared For {p.contact_name || p.business_name}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold leading-[1.15] mb-6" style={{ color: brandColor }} data-testid="hero-title">
                Your Partner in Payment Processing & Business Optimization
              </h1>
              <p className="text-gray-400 text-base lg:text-lg mb-12 leading-relaxed max-w-xl">
                We don't just process payments; we optimize your entire point-of-sale ecosystem to reduce costs, streamline operations, and accelerate funding.
              </p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                {[
                  { icon: CreditCard, title: 'Payment Processing', desc: 'Transparent, low-rate structures.' },
                  { icon: Monitor, title: 'POS Systems', desc: 'Clover, Square & Payanywhere.' },
                  { icon: Wrench, title: 'Installation', desc: 'White-glove local setup.' },
                  { icon: Lightbulb, title: 'Consulting', desc: 'Ongoing business optimization.' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brandColor}10` }}>
                      <Icon className="h-5 w-5" style={{ color: brandColor }} />
                    </div>
                    <div>
                      <p className="font-bold text-sm" style={{ color: brandColor }}>{title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Executive Overview */}
            <div className="bg-[#f4f5fa] rounded-2xl p-8 border border-[#e4e6f0]" data-testid="executive-overview">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--brand)]/50 mb-6">Proposal Summary</h3>
              <p className="text-sm font-semibold text-[var(--brand)] mb-5">Executive Overview</p>
              <div className="grid grid-cols-2 gap-3">
                <MetricCard label="Current Monthly Volume" value={`$${volume.toLocaleString()}`} />
                <MetricCard label="Current Eff. Rate" value={`${currentRate.toFixed(2)}%`} negative />
                <MetricCard label="Proposed Monthly Savings" value={`$${monthlySavings.toLocaleString()}`} positive />
                <MetricCard label="New Eff. Rate" value={`${isDualPricing ? '0.00' : newRate}%`} positive />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ WHAT WE FOUND ═══ */}
      <section id="analysis" className="py-16 lg:py-24 bg-[#f8f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[var(--brand)] mb-2" data-testid="analysis-heading">What We Found</h2>
          <p className="text-gray-400 mb-12 max-w-2xl">An honest breakdown of your current processing statement, exposing where your money is actually going.</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            {/* Current Breakdown */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-[var(--brand)] mb-1">Current Payment Breakdown</h3>
              <p className="text-sm text-gray-400 mb-8">Based on your recent statement analysis.</p>
              <div className="space-y-0">
                {[
                  { label: 'Monthly Volume', value: `$${volume.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: 'text-gray-800' },
                  { label: 'Total Fees Paid', value: `$${currentFees.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: 'text-red-600' },
                  { label: 'Effective Rate', value: `${currentRate.toFixed(2)}%`, color: 'text-red-600' },
                ].map(({ label, value, color }, i) => (
                  <div key={label} className={`flex justify-between items-center py-4 ${i < 2 ? 'border-b border-gray-100' : ''}`}>
                    <span className="text-gray-500 text-sm">{label}</span>
                    <span className={`text-xl font-bold ${color}`}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pie Chart */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-testid="fee-distribution-chart">
              <h3 className="text-lg font-bold text-[var(--brand)] mb-1">Fee Distribution</h3>
              <p className="text-sm text-gray-400 mb-6">Where your ${currentFees.toLocaleString(undefined, { minimumFractionDigits: 2 })} goes</p>
              <div className="flex items-center gap-8">
                <div className="w-44 h-44 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="value" stroke="none">
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-4 flex-1">
                  {pieData.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">{item.name}</p>
                        <p className="text-sm font-bold text-gray-900">${item.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-[11px] text-gray-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Areas of Concern */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h4 className="text-sm font-bold text-red-600 flex items-center gap-2 mb-4" data-testid="areas-of-concern">
              <AlertTriangle className="h-4 w-4" /> Areas of Concern
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                'High Processor Markup above interchange',
                `Hidden monthly PCI & statement fees ($${feeDist.hidden_fees.toFixed(0)}/mo)`,
                'Tiered pricing structure penalizing rewards cards',
              ].map((concern, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <X className="h-3 w-3 text-red-500" />
                  </div>
                  <span>{concern}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE SOLUTION ═══ */}
      <section id="recommendations" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[var(--brand)] mb-1" data-testid="solution-heading">The Solution</h2>
          <p className="text-lg font-semibold text-[var(--brand)]/80 mb-2">Your Optimized Payment Plan</p>
          <p className="text-gray-400 mb-12 max-w-2xl">Choose the structure that best fits your business model. We support top-tier hardware like Clover and Square.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {allOptions.map((option) => {
              const isSelected = option.model === pricing.model;
              const config = {
                dual_pricing: { tag: 'MOST POPULAR', tagBg: 'bg-amber-50 text-amber-700 border-amber-200' },
                flat_rate: { tag: 'PREDICTABLE', tagBg: 'bg-blue-50 text-blue-700 border-blue-200' },
                interchange_plus: { tag: 'TRANSPARENT', tagBg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
              };
              const c = config[option.model] || {};
              const benefits = {
                dual_pricing: ['Eliminate processing bills', 'Fully compliant signage', 'Automated at the POS'],
                flat_rate: ['Easy reconciliation', 'No statement fees', 'Great for small tickets'],
                interchange_plus: ['Lowest cost for high volume', 'Full transparency', 'Benefit from debit cards'],
              };
              const opts = benefits[option.model] || [];

              let displayRate;
              if (option.model === 'dual_pricing') displayRate = '$0 fees';
              else if (option.model === 'flat_rate') displayRate = `${((option.effective_rate || 0.0269) * 100).toFixed(1)}% +10\u00A2`;
              else displayRate = `IC +${(((option.effective_rate || 0.022) - 0.018) * 100).toFixed(2)}%`;

              return (
                <div
                  key={option.model}
                  className={`rounded-2xl p-7 border-2 transition-all relative ${isSelected ? 'border-[var(--brand)] shadow-xl' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`proposal-option-${option.model}`}
                >
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border ${c.tagBg}`}>
                    {c.tag}
                  </span>
                  <h3 className="text-xl font-extrabold text-[var(--brand)] mt-5 mb-1">
                    {(option.name || option.model).replace(' (Zero-Fee)', '')}
                  </h3>
                  <p className="text-3xl font-black text-emerald-600 mb-3">{displayRate}</p>
                  <p className="text-sm text-gray-400 mb-6 leading-relaxed">{option.description}</p>
                  <div className="space-y-3">
                    {opts.map((b, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                  {isSelected && (
                    <div className="absolute -top-3 right-4">
                      <span className="bg-[var(--brand)] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Your Plan
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ THE BOTTOM LINE ═══ */}
      <section className="py-16 lg:py-24 bg-[#0f5132] text-white" data-testid="bottom-line-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">The Bottom Line</h2>
          <p className="text-white/60 mb-12 max-w-2xl">
            By switching to our optimized {pricing.name || 'payment'} structure, here is the projected impact on your bottom line.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <p className="text-white/50 text-sm uppercase tracking-wider mb-1">Current Monthly Cost</p>
                <p className="text-3xl font-bold">${currentFees.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-white/50 text-sm uppercase tracking-wider mb-1">New Estimated Cost</p>
                <p className="text-4xl font-black">${newMonthlyCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-white/50 text-xs uppercase tracking-[0.2em] mb-3">Projected Annual Savings</p>
              <p className="text-5xl sm:text-6xl font-black tracking-tight">${annualSavings.toLocaleString()}</p>
              <p className="text-white/60 mt-4 text-lg">
                That's an extra <span className="font-bold text-white">${monthlySavings.toLocaleString()}</span> in your pocket every month.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ EQUIPMENT / BENEFITS ═══ */}
      <section id="equipment" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {qualifiesFreeEquipment ? (
            <>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[var(--brand)] mb-2" data-testid="equipment-heading">Modernize Your Point of Sale</h2>
              <p className="text-gray-400 mb-12 max-w-2xl">
                Sleek, powerful hardware options tailored to your workflow. Based on your volume, you qualify for <span className="font-bold text-emerald-600">free equipment placements</span>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
                {hw.map((device, idx) => (
                  <div key={device.key} className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-b from-emerald-50/40 to-white p-6 text-center" data-testid={`proposal-hw-${idx}`}>
                    <span className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                      FREE AT ${(device.free_volume_threshold || device.min_volume || 0).toLocaleString()} VOLUME
                    </span>
                    <div className="w-24 h-24 mx-auto my-5">
                      <img src={device.image} alt={device.name} className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <h4 className="font-extrabold text-[var(--brand)] text-lg">{device.name}</h4>
                    <p className="text-xs text-gray-400 mt-2 leading-relaxed">{device.features?.[0]}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[var(--brand)] mb-2" data-testid="benefits-heading">Included Benefits</h2>
              <p className="text-gray-400 mb-12 max-w-2xl">Every account comes with premium services to keep your business running smoothly.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
                {[
                  { icon: Users, title: 'Assigned Account Specialist', desc: 'A dedicated representative to handle all your processing needs and optimizations.' },
                  { icon: Printer, title: 'Complimentary Paper Supplies', desc: 'Free receipt paper and thermal supplies delivered directly to your business.' },
                  { icon: Settings, title: 'Free Installation & Implementation', desc: 'White-glove setup of your POS hardware and software at no additional cost.' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="rounded-2xl border border-gray-200 p-7 text-center hover:shadow-md transition-shadow">
                    <div className="w-14 h-14 rounded-xl bg-[var(--brand)]/[0.06] flex items-center justify-center mx-auto mb-5">
                      <Icon className="h-7 w-7 text-[var(--brand)]" />
                    </div>
                    <h4 className="font-bold text-[var(--brand)] text-base mb-2">{title}</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Universal Benefits */}
          <div className="bg-[#f4f5fa] rounded-2xl p-8 border border-[#e4e6f0]" data-testid="universal-benefits">
            <h3 className="text-lg font-bold text-[var(--brand)] mb-8 text-center">Every Merchant Receives</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Headphones className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-[var(--brand)]">24/7 Support</p>
                  <p className="text-sm text-gray-400 mt-1 leading-relaxed">Round-the-clock technical and account support whenever you need it.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Globe className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-bold text-[var(--brand)]">Preferred Language Support</p>
                  <p className="text-sm text-gray-400 mt-1 leading-relaxed">Dedicated support line available in your preferred language.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ COUNTDOWN + CTA ═══ */}
      <section id="cta" className="py-16 lg:py-24 bg-[var(--brand)]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          {timeLeft && !accepted && (
            <div className="mb-10" data-testid="countdown-timer">
              <p className="text-white/50 text-xs uppercase tracking-[0.2em] mb-5">
                {qualifiesFreeEquipment ? 'Your free equipment offer expires in' : 'This exclusive offer expires in'}
              </p>
              <div className="flex justify-center gap-4">
                {[
                  { val: timeLeft.hours, label: 'Hours' },
                  { val: timeLeft.minutes, label: 'Minutes' },
                  { val: timeLeft.seconds, label: 'Seconds' },
                ].map(({ val, label }) => (
                  <div key={label} className="bg-white/[0.08] backdrop-blur rounded-xl px-6 py-5 min-w-[90px] border border-white/[0.06]">
                    <p className="text-3xl font-black tabular-nums">{String(val).padStart(2, '0')}</p>
                    <p className="text-[10px] text-white/40 mt-1 uppercase tracking-wider">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!accepted ? (
            <>
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 leading-tight">
                Ready to save ${monthlySavings.toLocaleString()}/month?
              </h2>
              <p className="text-white/50 mb-10 text-lg max-w-xl mx-auto">
                {qualifiesFreeEquipment
                  ? 'Secure your free equipment placement and start saving today.'
                  : 'Lock in your savings — it only takes a few minutes to get started.'}
              </p>
              <button
                onClick={handleAccept}
                className="bg-emerald-500 hover:bg-emerald-400 text-white px-12 py-5 rounded-xl text-lg font-bold transition-all hover:scale-[1.03] shadow-2xl shadow-emerald-500/20 inline-flex items-center gap-3"
                data-testid="accept-proposal-btn"
              >
                Apply Now <ArrowRight className="h-5 w-5" />
              </button>
            </>
          ) : (
            <div className="py-8">
              <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-400 mb-5" />
              <h2 className="text-3xl font-extrabold mb-3">Proposal Accepted!</h2>
              <p className="text-white/50 text-lg">Your application is being processed. We'll be in touch shortly.</p>
            </div>
          )}
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-8 text-center" style={{ backgroundColor: brandColor, filter: 'brightness(0.7)' }}>
        <p className="text-white/25 text-xs">Generated {new Date(p.created_at).toLocaleDateString()} | Proposal ID: {p.proposal_id}</p>
        <p className="text-white/20 text-xs mt-1">Prepared exclusively for {p.business_name}</p>
      </footer>
    </div>
  );
}

// ── Metric Card ──
function MetricCard({ label, value, positive, negative }) {
  return (
    <div className={`bg-white rounded-xl p-5 shadow-sm ${positive ? 'ring-1 ring-emerald-200' : ''}`}>
      <p className={`text-xs mb-1.5 ${positive ? 'text-emerald-600 font-semibold' : 'text-gray-400'}`}>{label}</p>
      <div className="flex items-center gap-2">
        <p className={`text-xl font-extrabold ${positive ? 'text-emerald-600' : 'text-gray-800'}`}>{value}</p>
        {negative && <X className="h-5 w-5 text-red-500" />}
        {positive && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
      </div>
    </div>
  );
}
